import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, ChevronLeft, ChevronRight, Calendar as CalendarIcon, MapPin, Clock, Users, HandHelping, Car } from "lucide-react";
import { useTranslation } from "react-i18next";
import { modeOf } from "@/components/ModePicker";

const WEEKDAYS_SHORT = ["L", "M", "M", "G", "V", "S", "D"];
const MONTHS_FULL = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function isSameDay(a, b) { return a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }

export default function Calendar() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [selected, setSelected] = useState(() => toISO(new Date()));
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch wide range: 1 month before and 2 months after to support quick nav
      const from = toISO(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
      const to = toISO(new Date(cursor.getFullYear(), cursor.getMonth() + 2, 0));
      const { data } = await api.get("/rides", { params: { date_from: from, date_to: to, all_groups: true } });
      setRides(data.rides || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [cursor]);

  useEffect(() => { load(); }, [load]);

  // Map of date -> {driver: bool, passenger: bool, count: int, rides: [...]}
  const ridesByDate = useMemo(() => {
    const m = {};
    const uid = user?.id;
    for (const r of rides) {
      const role = r.driver_id === uid
        ? "driver"
        : (r.passengers || []).some((p) => p.user_id === uid)
        ? "passenger"
        : r.requester_id === uid
        ? "requester"
        : null;
      if (!role) continue; // only mark dates where the user is involved
      const cell = m[r.date] || { driver: false, passenger: false, requester: false, rides: [] };
      cell[role] = true;
      cell.rides.push({ ...r, _role: role });
      m[r.date] = cell;
    }
    return m;
  }, [rides, user?.id]);

  // Build month grid
  const monthGrid = useMemo(() => {
    const first = startOfMonth(cursor);
    const last = endOfMonth(cursor);
    // Monday-based weekday index (0..6)
    const startIdx = (first.getDay() + 6) % 7;
    const cells = [];
    // leading blanks
    for (let i = 0; i < startIdx; i++) cells.push(null);
    for (let day = 1; day <= last.getDate(); day++) {
      cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), day));
    }
    // trailing blanks to fill last row
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const selectedDayRides = (ridesByDate[selected]?.rides || []).slice().sort((a, b) => (a.time || "").localeCompare(b.time || ""));

  const today = new Date();
  const todayISO = toISO(today);

  return (
    <div className="anim-slide-up max-w-2xl mx-auto" data-testid="calendar-page">
      <Link to="/home" className="text-sm font-bold text-[#1A2A6C] inline-flex items-center gap-1 hover:underline mb-3" data-testid="calendar-back">
        <ArrowLeft className="w-4 h-4" /> {t("common.back")}
      </Link>

      <div className="flex items-center gap-3 mb-1">
        <div className="w-11 h-11 grid place-items-center rounded-2xl bg-[#F5F6FA] text-[#1A2A6C]">
          <CalendarIcon className="w-5 h-5" />
        </div>
        <h1 className="font-heading text-3xl font-black text-[#1A1D29] leading-tight">{t("calendar.title")}</h1>
      </div>
      <p className="text-sm text-[#8A91A8] mb-4">{t("calendar.subtitle")}</p>

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-2.5 mb-4" data-testid="calendar-quick-actions">
        <Link
          to={`/rides/new?date=${selected}`}
          className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-[#1A2A6C] text-white font-bold text-sm shadow-[0_6px_18px_rgba(26,42,108,0.22)] hover:bg-[#14205A] transition-all"
          data-testid="calendar-create-ride"
        >
          <Car className="w-4 h-4" /> {t("calendar.offerRide")}
        </Link>
        <Link
          to={`/rides/new?type=request&date=${selected}`}
          className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-[#FF9E1B] text-white font-bold text-sm shadow-[0_6px_18px_rgba(255,158,27,0.28)] hover:bg-[#E07A00] transition-all"
          data-testid="calendar-request-ride"
        >
          <HandHelping className="w-4 h-4" /> {t("calendar.requestRide")}
        </Link>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-3 flex-wrap" data-testid="calendar-legend">
        <span className="inline-flex items-center gap-2 text-xs font-semibold text-[#5A6178]">
          <span className="w-2.5 h-2.5 rounded-full bg-[#1A2A6C]" /> {t("calendar.legendDriver")}
        </span>
        <span className="inline-flex items-center gap-2 text-xs font-semibold text-[#5A6178]">
          <span className="w-2.5 h-2.5 rounded-full bg-[#FFC940]" /> {t("calendar.legendPassenger")}
        </span>
      </div>

      {/* Calendar card */}
      <div className="brut-card p-4 sm:p-5" data-testid="calendar-month">
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() => setCursor(addMonths(cursor, -1))}
            className="w-9 h-9 grid place-items-center rounded-xl bg-[#F5F6FA] text-[#1A2A6C] hover:bg-[#E5E9F5]"
            data-testid="calendar-prev"
            aria-label={t("calendar.prevMonth")}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <p className="font-heading text-lg font-bold text-[#1A1D29]" data-testid="calendar-month-label">
            {MONTHS_FULL[cursor.getMonth()]} {cursor.getFullYear()}
          </p>
          <button
            onClick={() => setCursor(addMonths(cursor, 1))}
            className="w-9 h-9 grid place-items-center rounded-xl bg-[#F5F6FA] text-[#1A2A6C] hover:bg-[#E5E9F5]"
            data-testid="calendar-next"
            aria-label={t("calendar.nextMonth")}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1.5 mb-1.5">
          {WEEKDAYS_SHORT.map((d, i) => (
            <span key={i} className="text-center text-[10px] font-bold uppercase tracking-widest text-[#8A91A8]">{d}</span>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1.5">
          {monthGrid.map((d, i) => {
            if (!d) return <div key={i} className="aspect-square" />;
            const iso = toISO(d);
            const info = ridesByDate[iso];
            const isToday = iso === todayISO;
            const isSelected = iso === selected;
            return (
              <button
                key={i}
                onClick={() => setSelected(iso)}
                className={`relative aspect-square rounded-xl flex flex-col items-center justify-center text-sm font-semibold transition-all ${
                  isSelected
                    ? "bg-[#1A2A6C] text-white shadow-[0_4px_12px_rgba(26,42,108,0.32)]"
                    : isToday
                    ? "bg-[#F5F6FA] text-[#1A2A6C] ring-2 ring-[#26B5A8]/40"
                    : "bg-white hover:bg-[#F5F6FA] text-[#1A1D29]"
                }`}
                data-testid={`calendar-day-${iso}`}
                data-has-driver={info?.driver ? "true" : undefined}
                data-has-passenger={info?.passenger ? "true" : undefined}
              >
                <span>{d.getDate()}</span>
                {info && (
                  <span className="absolute bottom-1.5 inline-flex items-center gap-0.5">
                    {info.driver && <span className={`w-1.5 h-1.5 rounded-full ${isSelected ? "bg-white" : "bg-[#1A2A6C]"}`} />}
                    {info.passenger && <span className={`w-1.5 h-1.5 rounded-full ${isSelected ? "bg-[#FFE9A8]" : "bg-[#FFC940]"}`} />}
                    {info.requester && !info.driver && !info.passenger && (
                      <span className={`w-1.5 h-1.5 rounded-full ${isSelected ? "bg-[#FFD8B5]" : "bg-[#FF9E1B]"}`} />
                    )}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected day rides */}
      <section className="mt-5" data-testid="calendar-day-rides">
        <h2 className="font-heading text-lg font-bold text-[#1A1D29] mb-3">
          {new Date(selected + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
        </h2>
        {loading ? (
          <p className="text-center text-[#8A91A8] py-4">Caricamento…</p>
        ) : selectedDayRides.length === 0 ? (
          <div className="brut-card p-6 text-center">
            <p className="text-[#8A91A8]">{t("calendar.noRidesDay")}</p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="calendar-day-rides-list">
            {selectedDayRides.map((r) => {
              const m = modeOf(r.mode);
              const ModeIcon = m.icon;
              const isDriver = r._role === "driver";
              const isPassenger = r._role === "passenger";
              const accent = isDriver ? "#1A2A6C" : isPassenger ? "#FFC940" : "#FF9E1B";
              const accentLabel = isDriver ? "Guidatore" : isPassenger ? "Passeggero" : "Richiesta";
              return (
                <li key={r.id}>
                  <Link
                    to={`/rides/${r.id}`}
                    className="block brut-card p-4 hover:border-[#1A2A6C]/30 transition-all"
                    style={{ borderLeft: `4px solid ${accent}` }}
                    data-testid={`calendar-ride-${r.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="brut-chip" style={{ backgroundColor: `${m.color}1F`, color: m.color }}>
                        <ModeIcon className="w-3 h-3" /> {m.label}
                      </span>
                      <span className="brut-chip" style={{ backgroundColor: `${accent}1F`, color: accent }}>
                        {accentLabel} · {r.time}
                      </span>
                    </div>
                    <p className="font-heading text-base font-bold text-[#1A1D29] truncate">{r.destination}</p>
                    {r.origin && <p className="text-xs text-[#8A91A8] truncate mt-0.5">{r.origin} → {r.destination}</p>}
                    {r.group_name && (
                      <p className="text-[11px] uppercase tracking-widest font-bold text-[#8A91A8] mt-2">📍 {r.group_name}</p>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
